import { animate, state, style, transition, trigger } from '@angular/animations';
import { LowerCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatError, MatFormField, MatHint } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatOption, MatSelect } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { isNil } from 'lodash-es';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, filter, switchMap, take, tap } from 'rxjs';
import { DialogService } from '../../../components/dialog/dialog.service';
import { unmetDependencies } from '../../../models/permission-dependencies.data';
import {
  ACTION_LABELS,
  PERMISSION_GROUPS,
  permissionLabel,
} from '../../../models/permission.model';
import { UserRole } from '../../../models/user-role.model';
import { PermissionService, UserService as ToolsetUserService } from '@ipg/ngx-frontend-toolset';
import { UserRoleService } from '../../../services/user-role.service';
import {
  UserCredentialsDialogComponent,
  UserCredentialsDialogData,
} from '../components/user-credentials-dialog/user-credentials-dialog.component';
import { UserService } from '../services/user.service';

interface PermissionGroupView {
  key: string;
  label: string;
  permissions: { key: string; value: string; actionLabel: string }[];
}

@Component({
  selector: 'app-user-detail',
  imports: [
    LowerCasePipe,
    ReactiveFormsModule,
    MatButton,
    MatIconButton,
    MatCheckbox,
    MatFormField,
    MatInput,
    MatIcon,
    MatError,
    MatHint,
    MatSelect,
    MatOption,
    MatProgressSpinner,
  ],
  templateUrl: './user-detail.component.html',
  styleUrl: './user-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('expandCollapse', [
      state('collapsed', style({ height: '0', opacity: '0', overflow: 'hidden' })),
      state('expanded', style({ height: '*', opacity: '1', overflow: 'hidden' })),
      transition('collapsed <=> expanded', [
        animate('150ms cubic-bezier(0.4, 0, 0.2, 1)'),
      ]),
    ]),
  ],
})
export class UserDetailComponent implements OnInit {
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _userService = inject(UserService);
  private readonly _roleService = inject(UserRoleService);
  private readonly _dialogService = inject(DialogService);
  private readonly _permissionService = inject(PermissionService);
  private readonly _toolsetUserService = inject(ToolsetUserService);

  /** Whether we're in create or edit mode */
  protected readonly mode = signal<'create' | 'edit'>('create');

  /** Loading state for edit mode */
  protected readonly loading = signal(false);

  /** Saving state */
  protected readonly saving = signal(false);

  /** Resetting password state */
  protected readonly resettingPassword = signal(false);

  /** The user ID when editing */
  private _userId: string | null = null;

  /** Whether we're editing our own account */
  protected readonly isSelfEdit = signal(false);

  /** Available roles */
  protected readonly roles = signal<UserRole[]>([]);

  /**
   * The roles to choose from, and the matrix of what one grants, need
   * `user_role:read`; this page itself only `user:read`. Without it neither
   * is fetched, and both say which permission would fill them.
   */
  protected readonly canReadRoles = computed(() =>
    this._permissionService.hasPermission('user_role:read')
  );
  protected readonly roleReadLabel = permissionLabel('user_role:read');

  /** Track which permission groups are expanded */
  protected readonly expandedGroups = signal(new Set<string>());

  /** Whether a role is currently selected */
  protected readonly hasRoleSelected = signal(false);

  /** Set of permissions from the selected role */
  protected readonly rolePermissions = signal(new Set<string>());

  /**
   * The role's permissions that do not work without others it lacks. The
   * role editor warns about them too; this is where someone asking why a
   * user cannot do something ends up.
   */
  protected readonly unmetRequirements = computed(() =>
    unmetDependencies(this.rolePermissions()).filter(
      (notice) => notice.strength === 'required'
    )
  );

  protected readonly displayNameControl = new FormControl('', {
    nonNullable: true,
    validators: [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(64),
    ],
  });

  protected readonly userRoleIdControl = new FormControl<string | null>(null, {
    validators: [Validators.required],
  });

  protected readonly isSuperUserControl = new FormControl(false, {
    nonNullable: true,
  });

  protected readonly form = new FormGroup({
    displayName: this.displayNameControl,
    userRoleId: this.userRoleIdControl,
    isSuperUser: this.isSuperUserControl,
  });

  /** All permission groups */
  private readonly _allPermissionGroups: PermissionGroupView[] = Object.entries(
    PERMISSION_GROUPS
  ).map(([key, group]) => ({
    key,
    label: group.label,
    permissions: Object.entries(group.permissions).map(([permKey, permValue]) => {
      const action = permValue.split(':')[1];
      return {
        key: permKey,
        value: permValue,
        actionLabel: ACTION_LABELS[action] ?? action,
      };
    }),
  }));

  /** Permission groups (not filtered — read-only display) */
  protected readonly permissionGroups = computed(() => this._allPermissionGroups);

  /** Page title */
  protected readonly pageTitle = computed(() =>
    this.mode() === 'create' ? 'Nieuwe gebruiker' : 'Gebruiker bewerken'
  );

  /** The currently selected role object */
  protected readonly selectedRole = signal<UserRole | null>(null);

  /** Count of selected (role) permissions */
  protected readonly selectedPermissionCount = computed(
    () => this.rolePermissions().size
  );

  /** Total number of available permissions */
  protected readonly totalPermissionCount = this._allPermissionGroups.reduce(
    (sum, group) => sum + group.permissions.length,
    0
  );

  constructor() {
    // Load roles once `user_role:read` is known to be held: permissions arrive
    // with the user fetch, which `authGuard` does not await, so a check made
    // at init was routinely false for users who do hold it.
    toObservable(this.canReadRoles)
      .pipe(
        filter(Boolean),
        take(1),
        switchMap(() =>
          this._roleService.getList({ params: { sortBy: 'label', sortOrder: 'asc' } })
        ),
        takeUntilDestroyed()
      )
      .subscribe((roles) => this.roles.set(roles));
  }

  ngOnInit(): void {
    const userId = this._route.snapshot.paramMap.get('userId');

    // Initialize all groups as expanded
    const allKeys = new Set(this._allPermissionGroups.map((g) => g.key));
    this.expandedGroups.set(allKeys);

    if (userId) {
      this.mode.set('edit');
      this._userId = userId;

      // Check if editing own account
      const currentUser = this._toolsetUserService.data() as Record<string, unknown> | null;
      if (currentUser && (currentUser['_id'] === userId)) {
        this.isSelfEdit.set(true);
        this.userRoleIdControl.disable();
        this.isSuperUserControl.disable();
      }

      this._loadUser(userId);
    }
  }

  protected navigateBack(): void {
    this._router.navigate(['../'], { relativeTo: this._route });
  }

  // --- Expand / Collapse ---

  protected isGroupExpanded(groupKey: string): boolean {
    return this.expandedGroups().has(groupKey);
  }

  protected toggleGroupExpanded(groupKey: string): void {
    this.expandedGroups.update((set) => {
      const next = new Set(set);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  }

  protected expandAll(): void {
    const allKeys = new Set(this._allPermissionGroups.map((g) => g.key));
    this.expandedGroups.set(allKeys);
  }

  protected collapseAll(): void {
    this.expandedGroups.set(new Set());
  }

  // --- Permission helpers (read-only) ---

  protected isPermissionSelected(permission: string): boolean {
    return this.rolePermissions().has(permission);
  }

  protected isGroupAllSelected(group: PermissionGroupView): boolean {
    const selected = this.rolePermissions();
    return group.permissions.every((p) => selected.has(p.value));
  }

  protected isGroupIndeterminate(group: PermissionGroupView): boolean {
    const selected = this.rolePermissions();
    const count = group.permissions.filter((p) => selected.has(p.value)).length;
    return count > 0 && count < group.permissions.length;
  }

  protected groupSelectedCount(group: PermissionGroupView): number {
    const selected = this.rolePermissions();
    return group.permissions.filter((p) => selected.has(p.value)).length;
  }

  // --- Role selection ---

  protected handleRoleChange(roleId: string | null): void {
    this.hasRoleSelected.set(roleId !== null);

    if (!roleId) {
      this.selectedRole.set(null);
      this.rolePermissions.set(new Set());
      return;
    }

    const role = this.roles().find((r) => r._id === roleId);
    if (role) {
      this.selectedRole.set(role);
      this.rolePermissions.set(new Set(role.permissions));
    } else if (this.canReadRoles()) {
      // Fetch the role if not in local cache (the list may still be loading)
      this._roleService.getById(roleId).subscribe({
        next: (fetchedRole) => {
          this.selectedRole.set(fetchedRole);
          this.rolePermissions.set(new Set(fetchedRole.permissions));
        },
      });
    }
  }

  // --- Save ---

  protected handleSave(): void {
    if (this.form.invalid || this.saving()) {
      return;
    }

    this.saving.set(true);

    const payload = {
      displayName: this.displayNameControl.value,
      userRoleId: this.userRoleIdControl.value,
      isSuperUser: this.isSuperUserControl.value,
    };

    if (this.mode() === 'create') {
      this._userService
        .create(payload)
        .pipe(
          tap(() => this.saving.set(false)),
          catchError(() => {
            this.saving.set(false);
            this._dialogService.alert({
              title: 'Foutmelding',
              message:
                'Er is iets misgegaan bij het aanmaken van de gebruiker. Probeer het later nog eens.',
              confirmText: 'OK',
            });
            return EMPTY;
          })
        )
        .subscribe({
          next: (response) => {
            this._showCredentialsDialog({
              title: 'Gebruiker aangemaakt',
              message: 'De gebruiker is succesvol aangemaakt.',
              accountId: response.accountId,
              temporaryPassword: response.temporaryPassword,
            })
              .pipe(tap(() => this.navigateBack()))
              .subscribe();
          },
        });
    } else {
      this._userService
        .update(this._userId!, payload)
        .pipe(
          tap(() => {
            this.saving.set(false);
            this.navigateBack();
          }),
          catchError(() => {
            this.saving.set(false);
            this._dialogService.alert({
              title: 'Foutmelding',
              message:
                'Er is iets misgegaan bij het bewerken van de gebruiker. Probeer het later nog eens.',
              confirmText: 'OK',
            });
            return EMPTY;
          })
        )
        .subscribe();
    }
  }

  // --- Reset password ---

  protected handleResetPassword(): void {
    if (!this._userId || this.resettingPassword()) {
      return;
    }

    this._dialogService
      .confirm({
        title: 'Wachtwoord resetten',
        message: `Weet je zeker dat je het wachtwoord wilt resetten?`,
        confirmText: 'Resetten',
        cancelText: 'Annuleren',
        onConfirm: () =>
          this._userService.resetPassword(this._userId!).pipe(
            catchError(() => {
              this._dialogService.alert({
                title: 'Foutmelding',
                message:
                  'Er is iets misgegaan bij het resetten van het wachtwoord. Probeer het later nog eens.',
                confirmText: 'OK',
              });
              return EMPTY;
            })
          ),
      })
      .afterClosed()
      .pipe(filter((response) => !isNil(response)))
      .subscribe({
        next: (response: UserCredentialsDialogData) => {
          this._showCredentialsDialog({
            title: 'Wachtwoord gereset',
            message: 'Het wachtwoord is succesvol gereset.',
            accountId: response.accountId,
            temporaryPassword: response.temporaryPassword,
          });
        },
      });
  }

  // --- Private ---

  private _showCredentialsDialog(data: UserCredentialsDialogData) {
    return this._dialogService
      .open<
        UserCredentialsDialogComponent,
        UserCredentialsDialogData
      >(UserCredentialsDialogComponent, { ...data }, { disableClose: true })
      .afterClosed();
  }

  private _loadUser(userId: string): void {
    this.loading.set(true);
    this._userService
      .getById(userId)
      .pipe(
        tap((user) => {
          this.displayNameControl.setValue(user.displayName);
          this.userRoleIdControl.setValue(user.userRoleId ?? null);
          this.isSuperUserControl.setValue(user.isSuperUser ?? false);

          // Load role permissions if a role is assigned
          if (user.userRoleId) {
            this.handleRoleChange(user.userRoleId);
          }

          this.loading.set(false);
        }),
        catchError(() => {
          this.loading.set(false);
          this._dialogService.alert({
            title: 'Foutmelding',
            message: 'Kon de gebruiker niet laden. Probeer het later nog eens.',
            confirmText: 'OK',
          });
          this.navigateBack();
          return EMPTY;
        })
      )
      .subscribe();
  }
}
